import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Edit2, Save, Folder, Trash2, Check, MoveRight, CheckSquare, Square, ArrowUpDown, ChevronUp, ChevronDown, Database, Settings } from "lucide-react";

const Menu = () => {
    const { user } = useAuth();
    const isAdmin = user.role === "admin";
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [allergens, setAllergens] = useState([]);
    const [activeCat, setActiveCat] = useState(null);
    const [editing, setEditing] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showNewCat, setShowNewCat] = useState(false);
    const [quickBuildOpen, setQuickBuildOpen] = useState(false);
    const [renaming, setRenaming] = useState(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [moveTarget, setMoveTarget] = useState("");
    const [dragCatTarget, setDragCatTarget] = useState(null);
    const [reorderModal, setReorderModal] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [reorderBusy, setReorderBusy] = useState(false);
    const [catSettingsFor, setCatSettingsFor] = useState(null);

    const openReorder = () => {
        setReorderList(categories.map((c) => ({ ...c })));
        setReorderModal(true);
    };

    const moveCatUp = (idx) => {
        if (idx <= 0) return;
        setReorderList((list) => {
            const copy = [...list];
            [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
            return copy;
        });
    };

    const moveCatDown = (idx) => {
        setReorderList((list) => {
            if (idx >= list.length - 1) return list;
            const copy = [...list];
            [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
            return copy;
        });
    };

    const saveReorder = async () => {
        setReorderBusy(true);
        try {
            const ordered_ids = reorderList.map((c) => c.id);
            await api.post("/menu/categories/reorder", { ordered_ids });
            toast.success("Ordine categorie aggiornato");
            setReorderModal(false);
            await load();
        } catch (e) {
            toast.error(formatError(e));
        }
        setReorderBusy(false);
    };

    const dropOnCategory = async (e, cid) => {
        e.preventDefault();
        setDragCatTarget(null);
        const itemId = e.dataTransfer.getData("text/plain");
        if (!itemId || cid === activeCat) return;
        try {
            await api.post("/menu/items/move", { item_ids: [itemId], category_id: cid });
            const cat = categories.find((c) => c.id === cid);
            toast.success(`Piatto spostato in "${cat?.name || ""}"`);
            load();
        } catch (err) { toast.error(formatError(err)); }
    };

    const load = async () => {
        const [c, i, a] = await Promise.all([
            api.get("/menu/categories"), api.get("/menu/items"), api.get("/allergens"),
        ]);
        setCategories(c.data); setItems(i.data); setAllergens(a.data);
        if (!activeCat && c.data.length) setActiveCat(c.data[0].id);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = items.filter((i) => i.category_id === activeCat);
    const activeCatObj = categories.find((c) => c.id === activeCat);

    const toggleAvailable = async (it) => {
        await api.patch(`/menu/items/${it.id}`, { available: !it.available });
        load();
    };

    const saveItem = async (data) => {
        try {
            if (data.id) {
                const { id, _id, category_name, ...rest } = data;
                await api.patch(`/menu/items/${id}`, rest);
                toast.success("Piatto aggiornato");
            } else {
                await api.post("/menu/items", data);
                toast.success(data.category_name ? `Piatto creato (categoria "${data.category_name}" auto-creata)` : "Piatto creato");
            }
            setEditing(null); setShowNew(false);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const removeItem = async (id) => {
        if (!window.confirm("Eliminare il piatto?")) return;
        await api.delete(`/menu/items/${id}`);
        load();
    };

    const createCategory = async (name, department) => {
        try {
            const { data } = await api.post("/menu/categories", { name: name.trim(), department });
            toast.success("Categoria creata");
            setShowNewCat(false);
            await load();
            setActiveCat(data.id);
        } catch (e) { toast.error(formatError(e)); }
    };

    const renameCategory = async (cid, newName) => {
        if (!newName?.trim()) return;
        try {
            await api.patch(`/menu/categories/${cid}`, { name: newName.trim() });
            toast.success("Categoria rinominata");
            setRenaming(null);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const deleteCategory = async (cid) => {
        const cat = categories.find((c) => c.id === cid);
        if (!cat) return;
        const count = items.filter((i) => i.category_id === cid).length;
        if (count > 0) {
            toast.error(`Impossibile eliminare: "${cat.name}" contiene ${count} piatti. Spostali o eliminali prima.`);
            return;
        }
        if (!window.confirm(`Eliminare la categoria "${cat.name}"?`)) return;
        try {
            await api.delete(`/menu/categories/${cid}`);
            toast.success("Categoria eliminata");
            // se era attiva, passa alla prima rimanente
            if (activeCat === cid) {
                const next = categories.find((c) => c.id !== cid);
                setActiveCat(next?.id || null);
            }
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    // ---- Multi-select / sposta ----
    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAllVisible = () => {
        const ids = new Set(filtered.map((i) => i.id));
        setSelected(ids);
    };
    const clearSelection = () => setSelected(new Set());
    const exitSelectMode = () => { setSelectMode(false); clearSelection(); setMoveTarget(""); };
    const confirmMove = async () => {
        if (!moveTarget || selected.size === 0) return;
        if (moveTarget === activeCat) {
            toast.error("I piatti sono già in questa categoria");
            return;
        }
        try {
            const { data } = await api.post("/menu/items/move", {
                item_ids: Array.from(selected),
                category_id: moveTarget,
            });
            toast.success(`${data.moved} ${data.moved === 1 ? "piatto spostato" : "piatti spostati"} in "${data.category_name}"`);
            exitSelectMode();
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="menu-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Carta</div>
                    <h1 className="font-display text-4xl font-bold">Menu</h1>
                </div>
                {isAdmin && (
                    <div className="flex gap-2 flex-wrap">
                        <button onClick={() => { setSelectMode((v) => !v); clearSelection(); }} data-testid="toggle-select-mode"
                            className={`h-12 px-4 rounded-md flex items-center gap-2 text-sm font-medium border ${
                                selectMode ? "bg-purple-900/30 border-purple-500 text-purple-200" : "border-[#1f2c44] text-elegant hover:border-brand"
                            }`}
                            title="Seleziona più piatti per spostarli">
                            <MoveRight className="w-4 h-4" /> {selectMode ? "Esci selezione" : "Sposta piatti"}
                        </button>
                        <button onClick={openReorder} data-testid="reorder-categories-btn"
                            disabled={categories.length < 2}
                            className="h-12 px-4 rounded-md flex items-center gap-2 text-sm font-medium border border-[#1f2c44] text-elegant hover:border-amber-500 hover:text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Riordina categorie (cambia l'ordine sui terminali)">
                            <ArrowUpDown className="w-4 h-4" /> Ordine portate
                        </button>
                        <button onClick={() => setShowNewCat(true)} data-testid="add-category-button"
                            className="h-12 px-5 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand/10">
                            <Folder className="w-4 h-4" /> Nuova categoria
                        </button>
                        <button onClick={() => setQuickBuildOpen(true)} data-testid="quick-build-btn"
                            className="h-12 px-4 rounded-md flex items-center gap-2 text-sm font-medium border border-emerald-500 text-emerald-300 hover:bg-emerald-900/20"
                            title="Aggiungi rapidamente più piatti dalla Libreria">
                            <Database className="w-4 h-4" /> Da Libreria
                        </button>
                        <button onClick={() => setShowNew(true)} data-testid="add-menu-item-button"
                            className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2">
                            <Plus className="w-4 h-4" /> Nuovo Piatto
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs categorie con azioni inline su quella attiva */}
            <div className="flex flex-wrap gap-2 items-center" data-testid="category-tabs">
                {categories.map((c) => {
                    const isActive = activeCat === c.id;
                    const isRenaming = renaming === c.id;
                    if (isRenaming) {
                        return (
                            <CategoryRenameInput
                                key={c.id}
                                initial={c.name}
                                onCancel={() => setRenaming(null)}
                                onConfirm={(name) => renameCategory(c.id, name)}
                            />
                        );
                    }
                    return (
                        <div key={c.id} className="flex items-stretch"
                            onDragOver={(e) => { if (!selectMode && isAdmin && c.id !== activeCat) { e.preventDefault(); setDragCatTarget(c.id); } }}
                            onDragLeave={() => { if (dragCatTarget === c.id) setDragCatTarget(null); }}
                            onDrop={(e) => dropOnCategory(e, c.id)}
                        >
                            <button onClick={() => setActiveCat(c.id)} data-testid={`menu-cat-${c.id}`}
                                className={`px-4 h-12 rounded-l-md border text-sm font-medium transition ${
                                    dragCatTarget === c.id
                                        ? "border-purple-500 bg-purple-900/30 text-purple-100 ring-2 ring-purple-500/50 scale-105"
                                        : isActive
                                        ? "border-brand bg-brand/10 text-brand"
                                        : "border-[#1f2c44] text-elegant"
                                } ${isAdmin && isActive ? "" : "rounded-r-md"}`}
                            >{c.name}</button>
                            {isAdmin && isActive && (
                                <div className="flex border border-l-0 border-brand rounded-r-md bg-brand/10">
                                    <button onClick={() => setCatSettingsFor(c)} data-testid={`cat-settings-${c.id}`}
                                        title="Impostazioni categoria + applica a tutti i piatti"
                                        className="w-9 h-12 text-purple-300 hover:bg-purple-900/20 flex items-center justify-center border-r border-brand/40">
                                        <Settings className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => setRenaming(c.id)} data-testid={`rename-cat-${c.id}`}
                                        title="Rinomina categoria"
                                        className="w-9 h-12 text-brand hover:bg-brand/20 flex items-center justify-center border-r border-brand/40">
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => deleteCategory(c.id)} data-testid={`delete-cat-${c.id}`}
                                        title="Elimina categoria"
                                        className="w-9 h-12 text-red-400 hover:bg-red-900/20 flex items-center justify-center rounded-r-md">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {categories.length === 0 && (
                    <div className="text-sm text-elegant italic">Nessuna categoria. Creane una per iniziare.</div>
                )}
            </div>

            {isAdmin && !selectMode && categories.length > 1 && filtered.length > 0 && (
                <div className="text-[11px] text-elegant -mt-3 flex items-center gap-2 italic" data-testid="dnd-hint">
                    <MoveRight className="w-3 h-3 text-purple-300" />
                    Suggerimento: trascina un piatto su un altro tab categoria per spostarlo rapidamente.
                </div>
            )}

            {/* Piatti */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-24">
                {filtered.map((it) => {
                    const isSel = selected.has(it.id);
                    return (
                    <div key={it.id}
                        draggable={!selectMode && isAdmin}
                        onDragStart={(e) => {
                            if (selectMode || !isAdmin) return;
                            e.dataTransfer.setData("text/plain", it.id);
                            e.dataTransfer.effectAllowed = "move";
                        }}
                        className={`bg-surface border rounded-lg p-5 relative transition ${
                        selectMode && isSel ? "border-purple-500 ring-2 ring-purple-500/40" : (it.available ? "border-[#1f2c44]" : "border-[#1f2c44] opacity-50")
                    } ${!selectMode && isAdmin ? "cursor-grab active:cursor-grabbing hover:border-brand/40" : ""}`} data-testid={`menu-card-${it.id}`}>
                        {selectMode && (
                            <button
                                onClick={() => toggleSelect(it.id)}
                                data-testid={`select-${it.id}`}
                                className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-deep border-2 border-purple-500 text-purple-300 flex items-center justify-center z-10 shadow-lg"
                            >
                                {isSel ? <CheckSquare className="w-4 h-4 text-purple-200" /> : <Square className="w-4 h-4" />}
                            </button>
                        )}
                        <div className={selectMode ? "cursor-pointer" : ""}
                             onClick={selectMode ? () => toggleSelect(it.id) : undefined}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="font-display font-bold text-lg flex-1">{it.name}</div>
                                <div className="font-display text-brand font-bold whitespace-nowrap">€ {it.price.toFixed(2)}</div>
                            </div>
                            <div className="text-xs text-elegant min-h-[2rem]">{it.description}</div>
                            <div className="flex flex-wrap gap-1 mt-3">
                                {it.allergens.map((a) => (
                                    <span key={a} className="text-[9px] uppercase tracking-wider bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded">{a}</span>
                                ))}
                            </div>
                        </div>
                        {!selectMode && (
                            <div className="flex gap-2 mt-4 pt-4 border-t border-[#1f2c44]">
                                <button onClick={() => toggleAvailable(it)} data-testid={`toggle-${it.id}`}
                                    className={`flex-1 h-10 rounded text-xs uppercase tracking-wider ${
                                        it.available ? "bg-green-900/30 text-green-400 border border-green-700/30" : "bg-red-900/30 text-red-400 border border-red-700/30"
                                    }`}>
                                    {it.available ? "Disponibile" : "Esaurito"}
                                </button>
                                {isAdmin && (
                                    <>
                                        <button onClick={() => setEditing(it)} className="w-10 h-10 rounded border border-[#1f2c44] hover:border-brand flex items-center justify-center" data-testid={`edit-${it.id}`}>
                                            <Edit2 className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => removeItem(it.id)} className="w-10 h-10 rounded border border-red-700/40 text-red-400 hover:bg-red-900/20 flex items-center justify-center">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );})}
                {filtered.length === 0 && activeCatObj && (
                    <div className="col-span-full text-sm text-elegant italic text-center py-12">
                        Nessun piatto in "{activeCatObj.name}". Aggiungine uno con "Nuovo Piatto".
                    </div>
                )}
            </div>

            {/* Sticky bar — modalità selezione */}
            {selectMode && (
                <div data-testid="move-bar"
                    className="fixed bottom-0 left-0 right-0 bg-deep border-t-2 border-purple-500/60 shadow-2xl z-40">
                    <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
                        <div className="text-sm">
                            <span className="font-bold text-purple-200" data-testid="selection-count">{selected.size}</span>
                            <span className="text-elegant"> {selected.size === 1 ? "piatto selezionato" : "piatti selezionati"}</span>
                        </div>
                        <button onClick={selectAllVisible} data-testid="select-all-visible"
                            className="h-10 px-3 text-xs uppercase tracking-wider border border-[#1f2c44] text-elegant rounded hover:border-brand hover:text-brand">
                            Sel. tutti ({filtered.length})
                        </button>
                        <button onClick={clearSelection} disabled={selected.size === 0} data-testid="clear-selection"
                            className="h-10 px-3 text-xs uppercase tracking-wider border border-[#1f2c44] text-elegant rounded hover:border-warm disabled:opacity-30">
                            Deselez. tutti
                        </button>
                        <div className="flex-1 min-w-[200px]" />
                        <span className="text-xs uppercase tracking-wider text-elegant">Sposta in:</span>
                        <select value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)}
                            data-testid="move-target"
                            className="h-10 px-3 bg-surface border border-[#1f2c44] rounded text-sm focus:border-brand focus:outline-none">
                            <option value="">— Categoria target —</option>
                            {categories.filter((c) => c.id !== activeCat).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <button onClick={confirmMove}
                            disabled={!moveTarget || selected.size === 0}
                            data-testid="confirm-move"
                            className="h-10 px-5 bg-purple-600 hover:bg-purple-500 text-white font-bold uppercase tracking-wider text-xs rounded flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                            <MoveRight className="w-4 h-4" /> Sposta
                        </button>
                        <button onClick={exitSelectMode} data-testid="exit-select-mode"
                            className="h-10 w-10 border border-[#1f2c44] text-elegant rounded hover:border-warm flex items-center justify-center">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {(editing || showNew) && (
                <ItemForm item={editing} categories={categories} allergens={allergens}
                    activeCat={activeCat}
                    onSave={saveItem} onClose={() => { setEditing(null); setShowNew(false); }} />
            )}

            {showNewCat && (
                <CategoryForm onCancel={() => setShowNewCat(false)} onCreate={createCategory} />
            )}

            {quickBuildOpen && (
                <QuickBuildFromLibrary
                    activeCategoryId={activeCat}
                    categories={categories}
                    onClose={() => setQuickBuildOpen(false)}
                    onDone={() => { setQuickBuildOpen(false); load(); }}
                />
            )}

            {catSettingsFor && (
                <CategorySettingsModal
                    category={catSettingsFor}
                    onClose={() => setCatSettingsFor(null)}
                    onSaved={() => { setCatSettingsFor(null); load(); }}
                />
            )}

            {/* Riordina categorie */}
            {reorderModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="reorder-modal">
                    <div className="bg-surface border-2 border-amber-500/70 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center gap-3 mb-3">
                            <ArrowUpDown className="w-7 h-7 text-amber-300" />
                            <div>
                                <div className="font-display text-xl font-bold">Ordine portate</div>
                                <div className="text-xs text-elegant">L'ordine si applica al terminale cameriere e a tutta l'app</div>
                            </div>
                        </div>

                        <div className="space-y-1 mb-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="reorder-list">
                            {reorderList.map((c, idx) => (
                                <div key={c.id}
                                    className="flex items-center gap-2 bg-deep border border-[#1f2c44] rounded px-3 py-2"
                                    data-testid={`reorder-row-${c.id}`}
                                >
                                    <div className="w-7 h-7 rounded-full bg-amber-900/30 border border-amber-500/50 text-amber-200 text-xs font-bold flex items-center justify-center flex-shrink-0">
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-warm font-medium truncate">{c.name}</div>
                                        <div className="text-[10px] text-elegant uppercase tracking-wider">{c.department}</div>
                                    </div>
                                    <button
                                        onClick={() => moveCatUp(idx)}
                                        disabled={idx === 0}
                                        data-testid={`reorder-up-${c.id}`}
                                        title="Sposta su"
                                        className="w-9 h-9 rounded border border-[#1f2c44] hover:border-amber-500 hover:text-amber-300 text-elegant disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => moveCatDown(idx)}
                                        disabled={idx === reorderList.length - 1}
                                        data-testid={`reorder-down-${c.id}`}
                                        title="Sposta giù"
                                        className="w-9 h-9 rounded border border-[#1f2c44] hover:border-amber-500 hover:text-amber-300 text-elegant disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setReorderModal(false)} data-testid="reorder-cancel"
                                disabled={reorderBusy}
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant disabled:opacity-50">
                                Annulla
                            </button>
                            <button onClick={saveReorder} disabled={reorderBusy} data-testid="reorder-save"
                                className="flex-1 h-12 bg-amber-500 hover:bg-amber-400 text-deep font-bold rounded uppercase tracking-wider text-xs disabled:opacity-60">
                                {reorderBusy ? "Salvataggio..." : "Salva nuovo ordine"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const CategoryRenameInput = ({ initial, onCancel, onConfirm }) => {
    const [v, setV] = useState(initial);
    const ref = useRef(null);
    useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
    return (
        <div className="flex items-stretch" data-testid="category-rename-input">
            <input
                ref={ref}
                value={v}
                onChange={(e) => setV(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") onConfirm(v);
                    if (e.key === "Escape") onCancel();
                }}
                className="px-4 h-12 rounded-l-md border border-brand bg-deep text-warm text-sm focus:outline-none w-48"
            />
            <button onClick={() => onConfirm(v)} className="w-9 h-12 border border-l-0 border-brand bg-brand/10 text-brand hover:bg-brand/20 flex items-center justify-center" data-testid="confirm-rename">
                <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={onCancel} className="w-9 h-12 border border-l-0 border-brand/60 bg-deep text-elegant hover:text-warm rounded-r-md flex items-center justify-center" data-testid="cancel-rename">
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

const CategoryForm = ({ onCancel, onCreate }) => {
    const [name, setName] = useState("");
    const [dept, setDept] = useState("cucina");
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="category-modal">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-display text-xl font-bold">Nuova categoria</h2>
                    <button onClick={onCancel}><X className="text-elegant" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Nome categoria</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Es. Primi del forno"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            data-testid="new-category-name"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Reparto / stampante</label>
                        <select
                            value={dept}
                            onChange={(e) => setDept(e.target.value)}
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            data-testid="new-category-dept"
                        >
                            <option value="cucina">Cucina</option>
                            <option value="pizzeria">Pizzeria</option>
                            <option value="bar">Bar</option>
                        </select>
                    </div>
                </div>
                <button
                    onClick={() => name.trim() && onCreate(name, dept)}
                    disabled={!name.trim()}
                    data-testid="confirm-new-category"
                    className="w-full h-12 mt-5 bg-brand text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-40"
                >
                    <Save className="w-4 h-4" /> Crea categoria
                </button>
            </div>
        </div>
    );
};

const ItemForm = ({ item, categories, allergens, activeCat, onSave, onClose }) => {
    const [form, setForm] = useState(
        item
            ? { ...item, tags: item.tags || [] }
            : { name: "", description: "", price: 0, category_id: activeCat || categories[0]?.id || "", allergens: [], available: true, course: "primo", department: "cucina", tags: [] }
    );
    const [tagPresets, setTagPresets] = useState([]);
    const [tagInUse, setTagInUse] = useState([]);
    const [newTag, setNewTag] = useState("");
    useEffect(() => {
        api.get("/menu/item-tags").then(({ data }) => {
            setTagPresets(data.presets || []);
            setTagInUse(data.in_use || []);
        }).catch(() => {});
    }, []);
    // Combobox categoria: opzione "__new__" mostra input testo per creare nuova categoria
    const [newCatName, setNewCatName] = useState("");
    const useNewCat = form.category_id === "__new__";

    const toggleAllergen = (a) => {
        setForm({ ...form, allergens: form.allergens.includes(a) ? form.allergens.filter((x) => x !== a) : [...form.allergens, a] });
    };

    const toggleTag = (t) => {
        const clean = t.trim();
        if (!clean) return;
        const cur = form.tags || [];
        const lc = clean.toLowerCase();
        const has = cur.some((x) => x.toLowerCase() === lc);
        setForm({
            ...form,
            tags: has ? cur.filter((x) => x.toLowerCase() !== lc) : [...cur, clean],
        });
    };

    const addFreeTag = () => {
        const clean = newTag.trim();
        if (!clean) return;
        toggleTag(clean);
        setNewTag("");
    };

    const submit = () => {
        if (!form.name.trim()) {
            toast.error("Inserisci il nome del piatto");
            return;
        }
        const payload = { ...form };
        if (useNewCat) {
            if (!newCatName.trim()) {
                toast.error("Digita il nome della nuova categoria");
                return;
            }
            delete payload.category_id;
            payload.category_name = newCatName.trim();
        }
        onSave(payload);
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="item-modal">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-display text-2xl font-bold">{item ? "Modifica" : "Nuovo"} Piatto</h2>
                    <button onClick={onClose}><X className="text-elegant" /></button>
                </div>
                <div className="space-y-3">
                    <input className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                        placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" />
                    <textarea className="w-full h-20 p-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                        placeholder="Descrizione" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="item-desc" />
                    <div className="grid grid-cols-2 gap-3">
                        <input type="number" step="0.50" className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            placeholder="Prezzo" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="item-price" />
                        <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            value={useNewCat ? "__new__" : (form.category_id || "")} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="item-category">
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            <option value="__new__">+ Nuova categoria…</option>
                        </select>
                        {useNewCat && (
                            <input
                                value={newCatName}
                                onChange={(e) => setNewCatName(e.target.value)}
                                placeholder="Nome nuova categoria (es. Primi del forno)"
                                className="col-span-2 h-12 px-3 bg-deep border-2 border-brand/50 rounded-md focus:border-brand focus:outline-none placeholder:text-elegant"
                                data-testid="item-new-category-name"
                                autoFocus
                            />
                        )}
                        <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                            <option value="cucina">Cucina</option>
                            <option value="pizzeria">Pizzeria</option>
                            <option value="bar">Bar</option>
                        </select>
                        <select className="h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                            value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}>
                            {["antipasto", "primo", "secondo", "contorno", "pizza", "dolce", "bevanda"].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-elegant mb-2">Allergeni</div>
                        <div className="flex flex-wrap gap-2">
                            {allergens.map((a) => (
                                <button key={a.id} type="button" onClick={() => toggleAllergen(a.id)}
                                    className={`px-3 py-1 rounded-full text-xs ${form.allergens.includes(a.id) ? "bg-brand text-deep" : "bg-surface-elevated border border-[#1f2c44] text-elegant"}`}>
                                    {a.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div data-testid="item-tags-section">
                        <div className="text-xs uppercase tracking-wider text-elegant mb-2 flex items-center justify-between">
                            <span>Tag piatto <span className="text-[10px] opacity-70 normal-case">(per filtri statistiche)</span></span>
                            {form.tags?.length > 0 && <span className="text-amber-300 text-[10px]">{form.tags.length} selezionati</span>}
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                            {tagPresets.map((t) => {
                                const active = form.tags?.some((x) => x.toLowerCase() === t.toLowerCase());
                                return (
                                    <button key={t} type="button" onClick={() => toggleTag(t)}
                                        data-testid={`tag-preset-${t.replace(/[^a-zA-Z0-9]/g,"-").toLowerCase()}`}
                                        className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${active ? "bg-amber-500 text-deep" : "bg-surface-elevated border border-[#1f2c44] text-elegant hover:border-amber-500/60"}`}>
                                        {t}
                                    </button>
                                );
                            })}
                            {/* Tag liberi già in uso ma non nei preset */}
                            {tagInUse.filter((t) => !tagPresets.some((p) => p.toLowerCase() === t.toLowerCase())).map((t) => {
                                const active = form.tags?.some((x) => x.toLowerCase() === t.toLowerCase());
                                return (
                                    <button key={`u-${t}`} type="button" onClick={() => toggleTag(t)}
                                        className={`px-3 py-1 rounded-full text-xs italic transition-colors ${active ? "bg-purple-500 text-deep" : "bg-surface-elevated border border-purple-500/30 text-purple-300 hover:border-purple-500/60"}`}>
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Custom tags già aggiunti al form ma non nei preset/in-use */}
                        {form.tags?.filter((t) => !tagPresets.some((p) => p.toLowerCase() === t.toLowerCase()) && !tagInUse.some((u) => u.toLowerCase() === t.toLowerCase())).map((t) => (
                            <span key={`new-${t}`} className="inline-flex items-center gap-1 mr-2 mb-2 px-3 py-1 rounded-full text-xs bg-amber-500/80 text-deep font-bold">
                                {t} <button type="button" onClick={() => toggleTag(t)} className="hover:text-red-700"><X className="w-3 h-3" /></button>
                            </span>
                        ))}
                        <div className="flex gap-2">
                            <input
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFreeTag())}
                                placeholder="Tag personalizzato (es. Speziato, Picante…)"
                                maxLength={40}
                                data-testid="item-tag-input"
                                className="flex-1 h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm focus:border-amber-500 focus:outline-none"
                            />
                            <button type="button" onClick={addFreeTag} data-testid="item-tag-add"
                                className="h-10 px-3 border border-amber-500/50 text-amber-300 hover:bg-amber-900/30 rounded text-xs font-bold uppercase tracking-wider">
                                + Aggiungi
                            </button>
                        </div>
                    </div>
                </div>
                <button onClick={submit} data-testid="save-item-button"
                    className="w-full h-14 mt-5 bg-brand text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" /> Salva
                </button>
            </div>
        </div>
    );
};

export default Menu;

// ============ CATEGORY SETTINGS MODAL ============
const COURSE_OPTIONS = [
    { id: "", label: "(non specificato)" },
    { id: "antipasto", label: "Antipasto" },
    { id: "primo", label: "Primo" },
    { id: "secondo", label: "Secondo" },
    { id: "contorno", label: "Contorno" },
    { id: "pizza", label: "Pizza" },
    { id: "dolce", label: "Dolce" },
    { id: "bevanda", label: "Bevanda" },
    { id: "caffe", label: "Caffè" },
];
const DEPT_OPTIONS = [
    { id: "cucina", label: "Cucina", color: "text-amber-300 border-amber-500/60 bg-amber-900/20" },
    { id: "bar", label: "Bar", color: "text-cyan-300 border-cyan-500/60 bg-cyan-900/20" },
    { id: "pizzeria", label: "Pizzeria", color: "text-red-300 border-red-500/60 bg-red-900/20" },
];
const PRINTER_OPTIONS = [
    { id: "auto", label: "Auto (segue reparto)" },
    { id: "kitchen", label: "Cucina (kitchen)" },
    { id: "pizza", label: "Pizzeria (pizza)" },
    { id: "bar", label: "Bar (bar)" },
    { id: "generic", label: "Generica (universale)" },
];

const CategorySettingsModal = ({ category, onClose, onSaved }) => {
    const [dept, setDept] = useState(category.department || "cucina");
    const [course, setCourse] = useState(category.course || "");
    const [printer, setPrinter] = useState(category.printer_destination || "auto");
    const [propagate, setPropagate] = useState(true);
    const [busy, setBusy] = useState(false);
    const [itemsCount, setItemsCount] = useState(null);

    useEffect(() => {
        let stop = false;
        (async () => {
            try {
                const { data } = await api.get(`/menu/items?menu_id=${category.menu_id || ""}`);
                if (stop) return;
                setItemsCount(data.filter((it) => it.category_id === category.id).length);
            } catch (e) { /* ignore */ }
        })();
        return () => { stop = true; };
    }, [category]);

    const save = async () => {
        setBusy(true);
        try {
            // 1) Aggiorna la categoria
            await api.patch(`/menu/categories/${category.id}`, {
                department: dept,
                course: course || null,
                printer_destination: printer,
            });
            // 2) Se propagate, applica a tutti i piatti della categoria
            if (propagate) {
                const fields = ["department", "printer_destination"];
                if (course) fields.push("course");
                const body = { fields };
                if (course) body.course = course;
                const { data } = await api.post(`/menu/categories/${category.id}/apply-to-items`, body);
                toast.success(`Categoria salvata · ${data.items_updated} piatti aggiornati`);
            } else {
                toast.success("Categoria salvata (piatti NON modificati)");
            }
            onSaved();
        } catch (e) { toast.error(formatError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
            <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-purple-500/60 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="category-settings-modal">
                <div className="flex items-center gap-2 mb-2">
                    <Settings className="w-5 h-5 text-purple-300" />
                    <div className="text-xs uppercase tracking-[0.3em] text-purple-300">Impostazioni categoria</div>
                </div>
                <div className="font-display text-2xl font-bold mb-1">{category.name}</div>
                <div className="text-xs text-elegant mb-5">
                    {itemsCount === null ? "Caricamento piatti..." : (
                        itemsCount === 0
                            ? <span>Nessun piatto in questa categoria</span>
                            : <span>Contiene <strong className="text-warm">{itemsCount} piatti</strong></span>
                    )}
                </div>

                {/* Reparto */}
                <div className="mb-4">
                    <label className="text-[10px] uppercase tracking-wider text-elegant block mb-2">Reparto + stampante</label>
                    <div className="grid grid-cols-3 gap-2">
                        {DEPT_OPTIONS.map((d) => (
                            <button key={d.id} onClick={() => setDept(d.id)}
                                data-testid={`cat-set-dept-${d.id}`}
                                className={`h-11 rounded-md text-xs uppercase tracking-wider font-bold border-2 transition-colors ${
                                    dept === d.id ? d.color : "border-[#1f2c44] text-elegant hover:border-[#3a4a66]"
                                }`}>
                                {d.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Portata */}
                <div className="mb-4">
                    <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Portata (course)</label>
                    <select value={course} onChange={(e) => setCourse(e.target.value)}
                        data-testid="cat-set-course"
                        className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-purple-500 focus:outline-none">
                        {COURSE_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <div className="text-[10px] text-elegant mt-1">Determina l&apos;ordine di servizio e raggruppa le statistiche (es. pizza/primo).</div>
                </div>

                {/* Stampante */}
                <div className="mb-5">
                    <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Destinazione stampa</label>
                    <select value={printer} onChange={(e) => setPrinter(e.target.value)}
                        data-testid="cat-set-printer"
                        className="w-full h-11 px-3 bg-deep border border-[#1f2c44] rounded text-warm focus:border-purple-500 focus:outline-none">
                        {PRINTER_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    <div className="text-[10px] text-elegant mt-1">Su quale stampante termica viene inviata la comanda.</div>
                </div>

                {/* Propaga */}
                <label className="flex items-start gap-3 p-3 mb-5 rounded-md cursor-pointer transition-colors border-2 border-purple-500/40 bg-purple-900/15 hover:bg-purple-900/25"
                    data-testid="cat-set-propagate-row">
                    <input type="checkbox" checked={propagate} onChange={(e) => setPropagate(e.target.checked)}
                        data-testid="cat-set-propagate"
                        className="mt-0.5 w-5 h-5 accent-purple-500" />
                    <div className="flex-1">
                        <div className="text-sm text-warm font-bold">Applica a TUTTI i piatti della categoria</div>
                        <div className="text-[11px] text-elegant mt-0.5">Sostituisce reparto, portata e destinazione stampa di {itemsCount ?? "—"} piatti. <strong className="text-purple-200">Operazione massiva.</strong></div>
                    </div>
                </label>

                <div className="flex gap-2">
                    <button onClick={onClose} disabled={busy} data-testid="cat-set-cancel"
                        className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold disabled:opacity-50">
                        Annulla
                    </button>
                    <button onClick={save} disabled={busy} data-testid="cat-set-save"
                        className="flex-[1.5] h-12 bg-purple-500 hover:bg-purple-400 disabled:bg-purple-900/30 disabled:cursor-not-allowed text-white font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors">
                        <Save className="w-4 h-4" /> {busy ? "Salvo..." : "Salva e applica"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============ QUICK BUILD FROM DISH LIBRARY ============
const QuickBuildFromLibrary = ({ activeCategoryId, categories, onClose, onDone }) => {
    const [dishes, setDishes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [fOrigin, setFOrigin] = useState("");
    const [fCourse, setFCourse] = useState("");
    const [selected, setSelected] = useState({});  // {dishId: {price, category_id}}
    const [bulkCategoryId, setBulkCategoryId] = useState(activeCategoryId || "");
    const [bulkNewCategoryName, setBulkNewCategoryName] = useState("");
    const [selectedDept, setSelectedDept] = useState("");

    const loadDishes = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (fOrigin) params.set("origin", fOrigin);
            if (fCourse) params.set("course", fCourse);
            const { data } = await api.get(`/dish-library?${params}`);
            setDishes(data.items || []);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
    };

    useEffect(() => { loadDishes(); /* eslint-disable-next-line */ }, [fOrigin, fCourse]);
    useEffect(() => {
        const t = setTimeout(() => loadDishes(), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const toggle = (dish) => {
        setSelected((prev) => {
            const copy = { ...prev };
            if (copy[dish.id]) { delete copy[dish.id]; }
            else { copy[dish.id] = { price: dish.suggested_price || "" }; }
            return copy;
        });
    };

    const updatePrice = (dishId, price) => {
        setSelected((prev) => ({ ...prev, [dishId]: { ...prev[dishId], price } }));
    };

    const selectedCount = Object.keys(selected).length;
    const selectedTotalQuick = useMemo(() => {
        return Object.values(selected).reduce((sum, v) => sum + (parseFloat(v.price) || 0), 0);
    }, [selected]);

    const submit = async () => {
        if (selectedCount === 0) { toast.error("Seleziona almeno un piatto"); return; }
        const targetCat = bulkCategoryId;
        const targetName = bulkNewCategoryName.trim();
        if (!targetCat && !targetName) { toast.error("Seleziona una categoria o creane una nuova"); return; }

        // Validazione prezzi
        const invalid = Object.entries(selected).filter(([, v]) => !v.price || parseFloat(v.price) <= 0);
        if (invalid.length > 0) {
            toast.error(`${invalid.length} piatti senza prezzo. Imposta un prezzo per tutti.`);
            return;
        }

        setBusy(true);
        let okCount = 0;
        let failCount = 0;
        try {
            for (const [dishId, conf] of Object.entries(selected)) {
                try {
                    await api.post(`/dish-library/${dishId}/add-to-menu`, {
                        category_id: targetCat || null,
                        category_name: targetCat ? null : targetName,
                        price: parseFloat(conf.price),
                    });
                    okCount += 1;
                } catch (e) {
                    failCount += 1;
                }
            }
            if (okCount > 0) toast.success(`${okCount} ${okCount === 1 ? "piatto aggiunto" : "piatti aggiunti"} al menu`);
            if (failCount > 0) toast.error(`${failCount} ${failCount === 1 ? "piatto fallito" : "piatti falliti"}`);
            onDone();
        } catch (e) { toast.error(formatError(e)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4" data-testid="quick-build-modal">
            <div className="bg-surface border-2 border-emerald-500 rounded-lg max-w-5xl w-full max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-[#1f2c44]">
                    <div className="flex items-center gap-3">
                        <Database className="w-7 h-7 text-emerald-300" />
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-300">Costruisci menu rapido</div>
                            <div className="font-display text-xl font-bold">Da Libreria Piatti</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-red-300"><X className="w-5 h-5" /></button>
                </div>

                {/* Filtri */}
                <div className="p-3 border-b border-[#1f2c44] flex flex-wrap gap-2 items-center">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca…"
                        data-testid="quick-build-search"
                        className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm flex-1 min-w-[180px] focus:outline-none focus:border-emerald-500" />
                    <select value={fOrigin} onChange={(e) => setFOrigin(e.target.value)}
                        className="h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutte origini</option>
                        <option value="italian">🇮🇹 Italiani</option>
                        <option value="international">🌍 Internazionali</option>
                    </select>
                    <select value={fCourse} onChange={(e) => setFCourse(e.target.value)}
                        className="h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-xs">
                        <option value="">Tutte portate</option>
                        {["antipasto", "primo", "secondo", "contorno", "dolce", "pizza", "bevanda"].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                {/* Grid piatti */}
                <div className="flex-1 overflow-y-auto p-3">
                    {loading ? (
                        <div className="text-center py-8 text-elegant text-sm">Caricamento…</div>
                    ) : dishes.length === 0 ? (
                        <div className="text-center py-8 text-elegant text-sm">Nessun piatto trovato. <a href="/piatti" className="text-emerald-300 underline">Carica seed dalla Libreria</a>.</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {dishes.map((d) => {
                                const isSel = !!selected[d.id];
                                return (
                                    <div key={d.id}
                                        onClick={() => toggle(d)}
                                        data-testid={`qb-dish-${d.id}`}
                                        className={`p-3 rounded border cursor-pointer transition-all ${isSel ? "border-emerald-500 bg-emerald-900/20" : "border-[#1f2c44] bg-deep hover:border-emerald-500/50"}`}>
                                        <div className="flex items-start gap-2 mb-1">
                                            <div className="flex-shrink-0 mt-0.5">
                                                {isSel
                                                    ? <CheckSquare className="w-4 h-4 text-emerald-400" />
                                                    : <Square className="w-4 h-4 text-elegant" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-warm text-sm leading-tight">{d.name}</div>
                                                <div className="text-[10px] text-elegant mt-0.5">
                                                    {d.course} · {d.department}
                                                    {d.origin === "italian" && " · 🇮🇹"}
                                                    {d.origin === "international" && " · 🌍"}
                                                </div>
                                            </div>
                                        </div>
                                        {isSel && (
                                            <div className="mt-2 ml-6" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center bg-[#0a1322] border border-emerald-500/40 rounded">
                                                    <span className="px-2 text-elegant text-xs">€</span>
                                                    <input type="number" step="0.10" min="0"
                                                        value={selected[d.id]?.price ?? ""}
                                                        onChange={(e) => updatePrice(d.id, e.target.value)}
                                                        placeholder="Prezzo"
                                                        data-testid={`qb-price-${d.id}`}
                                                        className="flex-1 h-9 bg-transparent text-warm text-sm font-mono focus:outline-none" />
                                                </div>
                                                {d.suggested_price && (
                                                    <button onClick={() => updatePrice(d.id, d.suggested_price)}
                                                        className="text-[9px] text-elegant mt-1 hover:text-emerald-300">
                                                        Usa suggerito: € {d.suggested_price.toFixed(2)}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer: categoria + azioni */}
                <div className="p-4 border-t border-[#1f2c44] bg-deep/50">
                    {selectedCount > 0 && (
                        <div className="text-xs text-elegant mb-2 flex items-center justify-between">
                            <span><strong className="text-emerald-300">{selectedCount}</strong> selezionati · Totale {selectedTotalQuick > 0 ? `€${selectedTotalQuick.toFixed(2)}` : "—"}</span>
                            <button onClick={() => setSelected({})} className="text-red-300 hover:text-red-200 text-[10px] uppercase tracking-wider">Pulisci selezione</button>
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="text-[10px] uppercase tracking-wider text-elegant">Aggiungi a:</div>
                        <select value={bulkCategoryId} onChange={(e) => setBulkCategoryId(e.target.value)}
                            data-testid="qb-cat-select"
                            className="h-10 px-2 bg-deep border border-[#1f2c44] rounded text-warm text-sm">
                            <option value="">— Nuova categoria —</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {!bulkCategoryId && (
                            <input value={bulkNewCategoryName} onChange={(e) => setBulkNewCategoryName(e.target.value)}
                                placeholder="Nome nuova categoria"
                                data-testid="qb-newcat-input"
                                className="h-10 px-3 bg-deep border border-[#1f2c44] rounded text-warm text-sm flex-1 min-w-[160px]" />
                        )}
                        <div className="flex-1"></div>
                        <button onClick={onClose} disabled={busy} className="h-10 px-4 border border-[#1f2c44] rounded text-elegant text-xs">Annulla</button>
                        <button onClick={submit} disabled={busy || selectedCount === 0}
                            data-testid="qb-submit"
                            className="h-10 px-5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded uppercase tracking-wider text-xs disabled:opacity-50 disabled:cursor-not-allowed">
                            {busy ? "Aggiungo…" : `Aggiungi ${selectedCount > 0 ? `(${selectedCount})` : ""}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
